import { useCallback } from 'react';

import { TagIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { getPlanTableLabel } from '@/modules/billing/utils';

type FormValues = { coupon: string };
const FORM_ID = 'coupon-form';

export const CouponFieldset = () => {
  const { t } = useTranslation();
  const { redeemPromoCode, customer, reedeemPromoCodeStatus } = useBilling();

  const coupon = customer?.discount?.coupon;

  const form = useForm<FormValues>({ mode: 'onChange', defaultValues: { coupon: '' } });
  const {
    handleSubmit,
    reset,
    setError,
    control,
    formState: { isDirty },
  } = form;

  const onSubmit = useCallback(
    ({ coupon }: FormValues) => {
      redeemPromoCode(coupon, {
        onSuccess: () => {
          reset({ coupon: '' });
        },
        onError: () => {
          setError('coupon', { message: t('billing.coupon_not_valid') });
        },
      });
    },
    [redeemPromoCode, reset, setError, t]
  );

  return (
    <div className="flex flex-col gap-2">
      <h2 className="px-1.5 text-sm font-medium text-foreground-muted">{t('billing.coupon')}</h2>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={reedeemPromoCodeStatus === 'pending'}
            disabled={!isDirty}
            saveButtonLabel={t('common.apply')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-6">
              <FormField
                control={control}
                name="coupon"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('billing.code')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('billing.coupon_placeholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {coupon && (
                <p className="flex items-center gap-1 text-sm">
                  <TagIcon className="text-foreground-muted" size={16} />
                  <span className="font-semibold">{coupon.name}</span>
                  {getPlanTableLabel(coupon.object)}
                  &nbsp;{t('billing.applied')}
                </p>
              )}
            </div>
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};
